import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { alpha, AppBar, Box, Button, Chip, Divider, Drawer, IconButton, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import CustomDialog from '@ui/components/common/Dialog';
import { useAddUserResponseDialogStyles } from '../AddUserResponseDialog/useAddUserResponseDialogStyles';
import { Airplane, Driving } from 'iconsax-react';
import EllipseIcon from '@ui/assets/custom-icons/ellipse-icon';
import { WheelChairIcon } from '@ui/assets/custom-icons/wheelChair-icon';
const FamilyInfoDialog = ({ open, onClose, persons }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const styles = useAddUserResponseDialogStyles();
    // Only show additional persons (index > 0)
    const familyMembers = persons.filter((_, idx) => idx > 0);
    const travelLabel = (mode) => {
        const m = (mode ?? '').toLowerCase().trim();
        if (m === 'auto')
            return t('familyForm.car', 'Car');
        if (m === 'bus')
            return t('familyForm.bus', 'Bus');
        return mode || '-';
    };
    const wheelchairLabel = (option) => {
        const v = (option ?? '').toLowerCase().trim();
        if (v === 'true')
            return t('familyForm.yes', 'Yes');
        if (v === 'false')
            return t('familyForm.no', 'No');
        return '-';
    };
    const accommodationLabel = (value) => {
        const v = (value ?? '').toLowerCase().trim();
        if (v === 'true')
            return t('familyForm.yes', 'Yes');
        if (v === 'false')
            return t('familyForm.no', 'No');
        return '-';
    };
    const translatedRelation = (rawValue) => {
        const relationKeyMap = {
            partner: 'Relation.Partner',
            kind: 'Relation.Child',
            familie: 'Relation.FamilyMember',
            vriend: 'Relation.Friend',
            collega: 'Relation.Colleague',
            grandchild: 'Relation.GrandChild',
            guest: 'Relation.Guest',
            other: 'Relation.Other'
        };
        const normalized = (rawValue ?? '').toLowerCase().trim();
        const key = relationKeyMap[normalized];
        return key ? String(t(key)) : String(rawValue ?? '').trim();
    };
    const formatDate = (dateStr) => {
        if (!dateStr)
            return '-';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime()))
                return dateStr;
            return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
        }
        catch {
            return dateStr;
        }
    };
    const personCard = (person, index) => {
        const name = [person.firstName, person.middleName, person.lastName]
            .filter((x) => (x ?? '').trim().length > 0)
            .join(' ');
        const relationship = translatedRelation(person.relationshipToYou || '');
        const dob = formatDate(person.DateOfBirth);
        const email = person.email || '-';
        const address = person.Address || '-';
        const travel = travelLabel(person.TravelMode);
        const isPlane = (person.TravelMode ?? '').toLowerCase().trim() === 'plane';
        const wheelchair = wheelchairLabel(person.Option);
        const accommodation = accommodationLabel(person.accommodationRequired);
        const travelIsActive = (person.TravelMode ?? '').trim() !== '';
        const wheelchairIsYes = (person.Option ?? '').toLowerCase().trim() === 'true';
        const accommodationIsYes = (person.accommodationRequired ?? '').toLowerCase().trim() === 'true';
        return (_jsxs(Box, { sx: {
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                backgroundColor: theme.palette.background.paper,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                boxShadow: theme.customShadows.card
            }, children: [_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1.5, children: [_jsx(Box, { sx: {
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                            }, children: _jsx(Typography, { fontSize: 12, fontWeight: 500, color: 'primary.main', children: String(index + 1).padStart(2, '0') }) }), _jsxs(Box, { children: [_jsx(Typography, { variant: 'subtitle1', color: 'text.primary', children: name || '-' }), relationship && (_jsx(Typography, { variant: 'caption', color: theme.palette.common.grey.dark, children: relationship }))] })] }), _jsx(Box, { children: _jsx(Divider, {}) }), _jsxs(Box, { display: 'flex', gap: 2, children: [_jsxs(Box, { flex: 1, children: [_jsx(Typography, { variant: 'caption', color: theme.palette.common.grey.dark, children: t('familyForm.dateOfBirth', 'Date of Birth') }), _jsx(Typography, { variant: 'body2', color: 'text.primary', children: dob })] }), _jsxs(Box, { flex: 1, children: [_jsx(Typography, { variant: 'caption', color: theme.palette.common.grey.dark, children: t('familyForm.email', 'Email') }), _jsx(Typography, { variant: 'body2', color: 'text.primary', children: email })] })] }), _jsxs(Box, { children: [_jsx(Typography, { variant: 'caption', color: theme.palette.common.grey.dark, children: t('familyForm.address', 'Address') }), _jsx(Typography, { variant: 'body2', fontWeight: 500, color: 'text.primary', children: address })] }), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'space-around', width: '100%', children: [_jsxs(Box, { sx: {
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }, children: [_jsx(Box, { children: isPlane ? (_jsx(Airplane, { variant: 'Outline', width: 24, height: 24, color: theme.palette.common.grey.dark })) : (_jsx(Driving, { variant: 'Outline', width: 24, height: 24, color: theme.palette.common.grey.dark })) }), _jsx(Chip, { size: 'small', variant: 'soft', color: travel === 'bus' ? 'warning' : 'default', label: travel })] }), _jsx(Box, { children: _jsx(EllipseIcon, {}) }), _jsxs(Box, { sx: {
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }, children: [_jsx(Box, { children: _jsx(WheelChairIcon, { size: 24, color: theme.palette.common.grey.dark }) }), _jsx(Chip, { size: 'small', variant: 'soft', color: wheelchairIsYes ? 'success' : 'default', label: wheelchair })] }), _jsx(Box, { children: _jsx(EllipseIcon, {}) }), _jsxs(Box, { sx: {
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }, children: [_jsx(Box, { children: _jsx(Icon, { icon: 'hugeicons:hotel-01', width: '24', height: '24', color: theme.palette.common.grey.dark }) }), _jsx(Chip, { size: 'small', variant: 'soft', color: accommodationIsYes ? 'success' : 'default', label: accommodation })] })] })] }, index));
    };
    const content = (_jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, px: 2, py: 2 }, children: familyMembers.length === 0 ? (_jsx(Typography, { variant: 'body1', color: 'text.secondary', textAlign: 'center', py: 4, children: t('familyForm.noFamilyMembers', 'No family members found.') })) : (familyMembers.map((person, idx) => personCard(person, idx))) }));
    const actions = (_jsx(Box, { children: _jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', fullWidth: true, onClick: onClose, children: t('actions.close', 'Close') }) }));
    if (isMobile) {
        return (_jsxs(Drawer, { anchor: 'bottom', open: open, onClose: onClose, PaperProps: { sx: styles.mobileDrawerStyle }, children: [_jsx(AppBar, { position: 'sticky', elevation: 0, sx: styles.appBarStyle, children: _jsxs(Toolbar, { sx: styles.mobileToolbarStyle, children: [_jsx(Typography, { variant: 'h6', color: 'text.primary', fontWeight: 600, children: t('familyForm.attendeesFamilyInfo', 'Attendees family info.') }), _jsx(IconButton, { onClick: onClose, size: 'small', sx: { border: 'none' }, children: _jsx(CloseIcon, {}) })] }) }), _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: content }), _jsx(Box, { sx: styles.footerBoxStyle, children: actions })] }));
    }
    return (_jsx(CustomDialog, { backgroundVariant: 'white', titleWithIcon: t('familyForm.attendeesFamilyInfo', 'Attendees family info.'), open: open, onClose: onClose, maxWidth: 'sm', titleIcon: '/assets/images/add-person.png', actions: actions, children: content }));
};
export default FamilyInfoDialog;
